import { aed, num } from "@/lib/format";
import { displayItemName } from "@/lib/menuLocale";
import {
  buildReportDocument,
  openReportPrint,
  periodMeta,
  resolveReportLetterhead,
  escapeReportHtml,
} from "@/lib/reportPrintShell";

function cell(value) {
  return value != null && value !== "" ? aed(value) : "";
}

async function buildHtml(title, report, metaHtml, tableHtml) {
  const company = await resolveReportLetterhead({ branchId: report?.branch_id, allBranches: report?.all_branches });
  return buildReportDocument({
    title,
    company,
    metaHtml,
    bodyHtml: tableHtml,
  });
}

export async function printCondensedStockReport(report) {
  if (!report) return false;
  const rows = (report.rows || [])
    .map(
      (r) => `
      <tr>
        <td class="mono">${r.item_code}</td>
        <td>${displayItemName(r)}</td>
        <td class="num">${num(r.opening_qty)}</td>
        <td class="num">${num(r.stock_in)}</td>
        <td class="num">${num(r.stock_out)}</td>
        <td class="num">${num(r.closing_qty)}</td>
        <td class="num">${cell(r.closing_value)}</td>
      </tr>`,
    )
    .join("");

  const html = await buildHtml(
    "Condensed Stock Report",
    report,
    periodMeta(report),
    `<table>
      <thead><tr><th>Code</th><th>Item</th><th>Opening</th><th>In</th><th>Out</th><th>Closing</th><th>Value</th></tr></thead>
      <tbody>${rows}</tbody>
    </table>`,
  );
  return openReportPrint(html);
}

export async function printOpeningStockReport(report) {
  if (!report) return false;
  const rows = (report.rows || [])
    .map(
      (r) => `
      <tr>
        <td class="mono">${r.item_code}</td>
        <td>${displayItemName(r)}</td>
        <td class="num">${num(r.opening_qty)}</td>
        <td class="num">${cell(r.cost_per_unit)}</td>
        <td class="num">${cell(r.opening_value)}</td>
      </tr>`,
    )
    .join("");

  const html = await buildHtml(
    "Opening Stock Report",
    report,
    `<div><strong>As of:</strong> ${escapeReportHtml(report.as_of_date)}</div>`,
    `<table>
      <thead><tr><th>Code</th><th>Item</th><th>Qty</th><th>Cost</th><th>Value</th></tr></thead>
      <tbody>${rows}</tbody>
    </table>`,
  );
  return openReportPrint(html);
}

export async function printPhysicalStockReport(report) {
  if (!report) return false;
  const rows = (report.rows || [])
    .filter((r) => r.physical_qty != null)
    .map(
      (r) => `
      <tr>
        <td class="mono">${r.item_code}</td>
        <td>${displayItemName(r)}</td>
        <td class="num">${num(r.book_qty)}</td>
        <td class="num">${num(r.physical_qty)}</td>
        <td class="num">${num(r.variance_qty)}</td>
      </tr>`,
    )
    .join("");

  const html = await buildHtml(
    "Physical Stock Report",
    report,
    `<div><strong>Count date:</strong> ${escapeReportHtml(report.count_date)}</div>`,
    `<table>
      <thead><tr><th>Code</th><th>Item</th><th>Book</th><th>Physical</th><th>Variance</th></tr></thead>
      <tbody>${rows}</tbody>
    </table>`,
  );
  return openReportPrint(html);
}

export async function printStockReport(report, type, options = {}) {
  const withBranch = report ? { ...report, branch_id: report.branch_id ?? options.branchId, all_branches: options.allBranches } : report;
  switch (type) {
    case "condensed":
      return printCondensedStockReport(withBranch);
    case "opening":
      return printOpeningStockReport(withBranch);
    case "physical":
      return printPhysicalStockReport(withBranch);
    default:
      return false;
  }
}
